// Ask AI's grounding on the Worklist and History: what the template's chat widget calls through the
// `window.AXAI_*` hooks in `index.html`. The page registers itself here while it is on screen;
// `index.html`'s fallbacks answer for every other screen.
//
// - The system prompt and the tools: `axai-prompt.ts`.
// - The tools read through `/api/routing/ask/*`, so the app's access and the Manager / Warehouse
//   split apply on the server; `apply_view` sets this grid (one step, Undo). None of them writes.
// - The order $ value tool only for a manager: a Warehouse user never gets it, not even in the
//   list of tools, and the prompt tells the model it has none.

import { useEffect, useRef } from "react";
import {
  ALL_HISTORY_CLEARS,
  PAGES,
  differsALot,
  fromSearch,
  limitTo,
  scopeOf,
  scopeWords,
  termsOf,
  withoutViewWords,
} from "@app/shared";
import type { AskQuery } from "@app/shared";
import { apiPost, useTableMeta } from "@axny/platform-ui";
import type { AskAiPage, AxaiApp, Tool } from "./axai-prompt";
import { askPath, describeView, systemPrompt, toolsFor } from "./axai-prompt";

export type { AskAiPage } from "./axai-prompt";

declare global {
  interface Window {
    __AXAI_APP__?: AxaiApp;
    AXAI_TOOLS?: Tool[];
  }
}

/** The bar's question and answer the panel continues from (Continue in chat); told to its model. */
let handoff: { question: string; answer: string } | null = null;

/**
 * Opens the widget's panel on the bar's question and answer. The panel's conversation is the
 * widget's own (inside the shared block): the two are shown as its bubbles, and its model is told
 * them in the system prompt, so a follow-up there knows what was asked.
 */
export function continueInChat(question: string, answer: string): void {
  handoff = { question, answer };
  const panel = document.getElementById("axai-panel");
  if (!panel?.classList.contains("open")) document.getElementById("axai-fab")?.click();
  const msgs = document.getElementById("axai-msgs");
  if (!msgs) return;
  for (const [role, text] of [
    ["user", question],
    ["bot", answer],
  ] as const) {
    const d = document.createElement("div");
    d.className = `axai-msg ${role}`;
    const av = document.createElement("div");
    av.className = "axai-av";
    av.textContent = role === "user" ? "You" : "✦";
    const bubble = document.createElement("div");
    bubble.className = "axai-bubble";
    bubble.textContent = text;
    bubble.style.whiteSpace = "pre-wrap";
    d.append(av, bubble);
    msgs.appendChild(d);
  }
  msgs.scrollTop = msgs.scrollHeight;
  document.getElementById("axai-text")?.focus();
}

/** What the tools take: the query, and the parts of the view a question leaves out or clears. */
type ToolInput = AskQuery & { pick_ticket?: string; line_id?: string; ignore_view?: string[]; clear?: string[] };

async function read(path: string, body: unknown): Promise<Record<string, unknown>> {
  try {
    return await apiPost<Record<string, unknown>>(askPath(path), body);
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * A count within the grid's view (`scopeOf`), as the page counts it: the page sends the lines it
 * keeps, and the same count without the view, to say beside it when they differ a lot. A count
 * about the other page (History from the Worklist) is that page as it opens.
 */
async function scoped(p: AskAiPage, path: "worklist" | "history" | "values", input: ToolInput): Promise<string> {
  // The scope is the page's to set, never the model's.
  const { ignore_view, within: _within, outside: _outside, clear: _clear, ...q } = input;
  const mode = path === "history" ? "history" : "worklist";
  if (mode !== p.mode) return JSON.stringify(await read(path, q));
  const scope = scopeOf(mode, p.view, q, ignore_view ?? []);
  const ids = p.lineIdsFor(scope);
  // Within My customers while it is on: the view's count and the one without it (`limitTo`).
  const all = p.allLineIds();
  const every = p.everyLineId();
  const mine = p.myCustomers.on ? "; my customers" : "";
  const words = scopeWords(mode, scope, p.headerOf) + mine;
  if (ids.length >= all.length)
    return JSON.stringify({ ...(await read(path, { ...q, ...limitTo(all, every) })), scope: words });
  const [inView, everywhere] = await Promise.all([
    read(path, { ...q, ...limitTo(ids, every) }),
    read(path, { ...q, limit: 0, ...limitTo(all, every) }),
  ]);
  if (inView.error || everywhere.error) return JSON.stringify(inView.error ? inView : everywhere);
  const count = path === "values" ? "lines" : "matched";
  return JSON.stringify({
    ...inView,
    scope: words,
    matched_without_view: everywhere[count],
    ...(path === "values" ? { total_value_without_view: everywhere.total_value } : {}),
    without_view: `across all ${withoutViewWords(mode, { ...scope.view, tab: scope.tab ?? "all" })}${mine ? ", within my customers" : ""}`,
    say_without_view: differsALot(Number(inView[count]), Number(everywhere[count])),
  });
}

/**
 * Registers this page with the widget while it is on screen. `page` is read at each question, so
 * the prompt and `apply_view` see the grid as it is then.
 */
export function useAskAi(page: AskAiPage): void {
  const ref = useRef(page);
  ref.current = page;
  const meta = useTableMeta(PAGES[page.mode].uses.reports[0]);
  const metaRef = useRef(meta.data);
  metaRef.current = meta.data;

  useEffect(() => {
    const app: AxaiApp = {
      systemPrompt: () => [
        ...systemPrompt(ref.current),
        ...(handoff
          ? [
              {
                type: "text" as const,
                text:
                  `In the Ask AI bar above the grid, the user asked "${handoff.question}" and was answered ` +
                  `"${handoff.answer}". They continue here: a follow-up may refer to it.`,
              },
            ]
          : []),
      ],
      handleToolCall: async (name, input, appendStatus, isStale) => {
        const p = ref.current;
        const q = (input ?? {}) as ToolInput;
        switch (name) {
          case "query_worklist":
            appendStatus("Reading the Worklist…");
            return scoped(p, "worklist", q);
          case "query_history":
            appendStatus("Reading History…");
            return scoped(p, "history", q);
          case "deposco_waiting":
            appendStatus("Reading the Deposco orders not on a line yet…");
            return JSON.stringify(await read("deposco-waiting", input ?? {}));
          case "explain_routing_window":
            appendStatus("Reading the routing window…");
            return JSON.stringify(await read("window", { pick_ticket: q.pick_ticket, line_id: q.line_id }));
          case "order_value":
            // Never offered to a Warehouse user; and the server refuses them as well.
            if (!p.manager) return JSON.stringify({ error: "Order values are for managers only." });
            appendStatus("Reading order values…");
            return scoped(p, "values", q);
          case "apply_view": {
            if (isStale()) return "Not applied: the conversation moved on.";
            // A customer named by its code, a division by its abbreviation (as the grid shows them),
            // is its name in the view: the grid's filters keep names.
            const f = q.filters;
            if (f?.customer_code?.length || f?.customer?.length) {
              const byCode = new Map(Object.entries(p.customerCodes).map(([k, v]) => [k.toLowerCase(), v]));
              f.customer = [
                ...new Set(
                  [...(f.customer ?? []), ...(f.customer_code ?? [])].map((c) => byCode.get(c.toLowerCase()) ?? c),
                ),
              ];
              delete f.customer_code;
            }
            if (f?.division_abbrev?.length) {
              const byAbbrev = new Map(Object.entries(p.divisionNames).map(([k, v]) => [k.toLowerCase(), v]));
              f.division = [
                ...new Set([
                  ...(f.division ?? []),
                  ...f.division_abbrev.flatMap((a) => byAbbrev.get(a.toLowerCase()) ?? [a]),
                ]),
              ];
              delete f.division_abbrev;
            }
            // A customer this page does not have would empty the grid: refused, with names like it.
            const unknown = (q.filters?.customer ?? []).filter(
              (c) => !p.customers.some((k) => k.toLowerCase() === c.toLowerCase()),
            );
            if (unknown.length) {
              const like = (c: string) =>
                p.customers.filter((k) => k.toLowerCase().includes(c.toLowerCase().slice(0, 4))).slice(0, 6);
              return JSON.stringify({
                applied: false,
                no_such_customer: Object.fromEntries(unknown.map((c) => [c, like(c)])),
              });
            }
            const meta = metaRef.current;
            const labelOf = (column: string) => meta?.columns.find((c) => c.name === column)?.label ?? column;
            // Only what the question is about changes; History's all_history clears its own filters.
            const clear = [...(q.clear ?? []), ...(p.mode === "history" && q.all_history ? ALL_HISTORY_CLEARS : [])];
            const next = fromSearch(p.mode, termsOf(q), p.view, labelOf, meta?.defaultSort, clear);
            p.apply(next.view, next.unapplied);
            // What the grid shows now, counted as the page counts it: say this number.
            const shows = p.shownFor(next.view);
            return JSON.stringify({
              applied: describeView({ ...p, view: next.view }),
              lines_shown: shows.shown,
              lines_per_tab: shows.counts,
              not_applied: next.unapplied,
              undo: "The user can undo it with the Undo button above the grid.",
            });
          }
          default:
            return JSON.stringify({ error: `Unknown tool ${name}` });
        }
      },
    };
    window.__AXAI_APP__ = app;
    document.body.classList.add("axai-ready");
    return () => {
      if (window.__AXAI_APP__ === app) delete window.__AXAI_APP__;
      window.AXAI_TOOLS = [];
      document.body.classList.remove("axai-ready");
    };
  }, []);

  // The tool list is the one part the widget reads as a value: kept in step with the page and role.
  useEffect(() => {
    window.AXAI_TOOLS = toolsFor(page.mode, page.manager);
  }, [page.mode, page.manager]);
}
